import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import DateInput from '../../components/forms/DateInput';
import TimeInput from '../../components/forms/TimeInput';
import '../../css/modals.css';

const emptyValues = (initialDate, initialStart, initialEnd) => ({
   start: initialStart || '', end: initialEnd || '', date: initialDate || '', notes: '',
});

// initialDate/initialStart/initialEnd ('yyyy-MM-dd'/'HH:mm') seed the form
// when opened from a shortcut that already knows the time (e.g. the chat's
// "create lesson from this reply" button) - blank/omitted behaves exactly
// as before. overlayClassName is an optional extra class on the backdrop -
// used by ChatComponent.jsx to outrank the floating chat's own z-index
// (1000) instead of raising .modal-update's z-index for all ~23 callers.
export default function LessonCreateModal({ isOpen, studentId, onClose, onCreated, initialDate, initialStart, initialEnd, overlayClassName }) {
   const navigate = useNavigate();
   const [values, setValues] = useState(emptyValues(initialDate, initialStart, initialEnd));
   const [saving, setSaving] = useState(false);

   useEffect(() => {
      if(isOpen) {
         setValues(emptyValues(initialDate, initialStart, initialEnd));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, initialDate, initialStart, initialEnd]);

   if(!isOpen) return '';

   const stopProp = e => e.stopPropagation();

   const handleFieldChange = (name, value) => {
      setValues(prev => ({...prev, [name]: value}));
   }

   const handleClose = () => {
      setValues(emptyValues());
      onClose();
   }

   const handleSubmit = (e) => {
      e.preventDefault();
      if(saving) return;
      setSaving(true);
      const payload = { student: String(studentId) };
      Object.keys(values).forEach(key => {
         if(values[key] !== '') {
            payload[key] = values[key];
         }
      });
      axios.post('/api/events', JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setValues(emptyValues());
         onCreated();
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => {
         setSaving(false);
      });
   }

   const lang = language[language.lang];

   return (
      <div className={"modal-update" + (overlayClassName ? " " + overlayClassName : "")} onClick={handleClose} style={{ display: 'flex' }}>
         <div className="modal-window" onClick={stopProp}>
            <h3 className="modal-w-title">{lang.modals.events.createTitle}</h3>
            <form onSubmit={handleSubmit}>
               <div className='student-lesson-create-fields'>
                  <div className='flex-from-input-wraper student-lesson-create-field' data-label={lang.start}>
                     <TimeInput value={values.start} onChange={(value) => handleFieldChange('start', value)} />
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field' data-label={lang.end}>
                     <TimeInput value={values.end} onChange={(value) => handleFieldChange('end', value)} />
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.date}>
                     <DateInput value={values.date} onChange={(value) => handleFieldChange('date', value)} />
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.notes}>
                     <input className='submit-field from-input txt-c' type='text'
                        placeholder={lang.notes}
                        value={values.notes} onChange={(e) => handleFieldChange('notes', e.target.value)} />
                  </div>
               </div>
               <button type='submit' className='form-button txt-c' disabled={saving} style={{marginTop:14}}>
                  {saving ? lang['Loading ...'] : lang.save}
               </button>
            </form>
         </div>
      </div>
   );
}
